import React, { useState, useEffect } from 'react';
import { DashboardWidget } from '../../services/dashboardConfigurationService';
import dashboardConfigurationService from '../../services/dashboardConfigurationService';
import './DynamicWidgetRenderer.css';

interface DynamicWidgetRendererProps {
  widget: DashboardWidget;
  selectedUserId?: number;
}

interface ChartData {
  labels: string[];
  datasets: any[];
}

const DynamicWidgetRenderer: React.FC<DynamicWidgetRendererProps> = ({ 
  widget, 
  selectedUserId 
}) => {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadWidgetData();
  }, [widget.id, selectedUserId]);

  const loadWidgetData = async () => {
    if (!widget.id) {
      setError('Widget ID is missing');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const widgetData = await dashboardConfigurationService.getWidgetData(widget.id);
      setData(widgetData);
      
    } catch (error: any) {
      console.error('Error loading widget data:', error);
      setError(error.message || 'Failed to load widget data');
    } finally {
      setLoading(false);
    }
  };

  const getGridStyle = () => ({
    gridColumn: `span ${Math.min(widget.width, 12)}`,
    gridRow: `span ${Math.min(widget.height, 6)}`,
    minHeight: `${widget.height * 80}px`
  });

  const renderMetricCard = () => {
    if (!data) return null;

    const value = data.value || 0;
    const label = data.label || widget.widgetName;
    const change = data.change || null;
    const trend = data.trend || null;

    return (
      <div className="metric-card-content">
        <div className="metric-value">
          {typeof value === 'number' ? value.toLocaleString() : value}
        </div>
        <div className="metric-label">{label}</div>
        {change !== null && (
          <div className={`metric-change ${change >= 0 ? 'positive' : 'negative'}`}>
            {change >= 0 ? '↗' : '↘'} {Math.abs(change)}%
          </div>
        )}
      </div>
    );
  };

  const renderKPISummary = () => {
    if (!data || !data.kpis) return null;

    return (
      <div className="kpi-summary-content">
        <h4>{widget.widgetName}</h4>
        <div className="kpi-list">
          {data.kpis.map((kpi: any, index: number) => (
            <div key={index} className="kpi-item">
              <div className="kpi-name">{kpi.name}</div>
              <div className="kpi-value">{kpi.value}</div>
              {kpi.change && (
                <div className={`kpi-change ${kpi.change >= 0 ? 'positive' : 'negative'}`}>
                  {kpi.change >= 0 ? '+' : ''}{kpi.change}%
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderTable = () => {
    if (!data || !data.columns || !data.rows) return null;

    return (
      <div className="table-content">
        <h4>{widget.widgetName}</h4>
        <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                {data.columns.map((column: string, index: number) => (
                  <th key={index}>{column}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.rows.map((row: any[], rowIndex: number) => (
                <tr key={rowIndex}>
                  {row.map((cell: any, cellIndex: number) => (
                    <td key={cellIndex}>{cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderInsightCard = () => {
    if (!data) return null;

    const title = data.title || widget.widgetName;
    const insight = data.insight || 'No insights available';
    const confidence = data.confidence || null;

    return (
      <div className="insight-card-content">
        <h4>{title}</h4>
        <div className="insight-text">{insight}</div>
        {confidence && (
          <div className="insight-confidence">
            Confidence: {Math.round(confidence * 100)}%
          </div>
        )}
      </div>
    );
  };

  const renderChart = () => {
    // For now, show a placeholder since we haven't implemented chart rendering yet
    return (
      <div className="chart-placeholder">
        <div className="chart-icon">📊</div>
        <h4>{widget.widgetName}</h4>
        <p>Chart Type: {widget.chartType}</p>
        <p>KPIs: {widget.dataConfig?.kpiCodes?.join(', ') || 'None selected'}</p>
        <div className="chart-coming-soon">
          📈 Chart rendering coming soon!
        </div>
      </div>
    );
  };

  const renderWidgetContent = () => {
    switch (widget.widgetType) {
      case 'metric_card':
        return renderMetricCard();
      case 'kpi_summary':
        return renderKPISummary();
      case 'table':
        return renderTable();
      case 'insight_card':
        return renderInsightCard();
      case 'chart':
        return renderChart();
      default:
        return (
          <div className="unknown-widget">
            <h4>{widget.widgetName}</h4>
            <p>Unknown widget type: {widget.widgetType}</p>
          </div>
        );
    }
  };

  if (loading) {
    return (
      <div className="dynamic-widget loading" style={getGridStyle()}>
        <div className="widget-loading">
          <div className="loading-spinner-small"></div>
          <span>Loading...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dynamic-widget error" style={getGridStyle()}>
        <div className="widget-error">
          <div className="error-icon">⚠️</div>
          <div className="error-message">{error}</div>
          <button onClick={loadWidgetData} className="retry-btn-small">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div 
      className={`dynamic-widget ${widget.widgetType}`} 
      style={getGridStyle()}
    >
      {renderWidgetContent()}
    </div>
  );
};

export default DynamicWidgetRenderer;
